<template>
  <div id="giftCard">
    <template v-if="giftCard">

      <Panel :title="$t('gift_card.single') + ' #' + giftCard.number" collapsible>
        <div class="columns">
          <div class="column">
            <InformationBlock v-if="giftCard.from"
                :title="$t('gift_card.from')"
                icon="/icon/user.svg"
                :data="[
                  {label: $t('name'), value: giftCard.from.name},
                  {label: $t('email'), value: giftCard.from.email },
                  {label: $t('note'), value: giftCard.from.note},
                ]"
            />
            <InformationBlock
                :title="$t('gift_card.info')"
                icon="/icon/gift.svg"
                :data="[
                  {label: $t('gift_card.purchase_date'), value: formatDateTime(giftCard.createdAt)},
                  {label: $t('gift_card.number'), value: giftCard.number},
                  {label: $t('currency'), value: giftCard.currency },
                ]"
            />
          </div>
          <div class="column">
            <InformationBlock v-if="giftCard.to"
                :title="$t('gift_card.to')"
                icon="/icon/user.svg"
                :data="[
                  {label: $t('name'), value: giftCard.to.name},
                  {label: $t('email'), value: giftCard.to.email },
                ]"
            />
            <InformationBlock
                :title="$t('gift_card.balance')"
                icon="/icon/wallet.svg"
                :data="[
                  {label: $t('gift_card.initial_balance'), value: $tc(giftCard.initialBalance) },
                  {label: $t('gift_card.current_balance'), value: $tc(giftCard.balance) },
                  ]"
              />
            <br>
            <button class="btn btn-primary" @click="adjustment">{{$t('gift_card.adjustment_button')}}</button>
          </div>
        </div>
      </Panel>

      <Panel :title="$t('loyalty_accounts.transactions')" collapsible>
        <div class="transactions" v-if="transactions && transactions.length > 0">
          <table>
            <thead>
              <tr>
                <th class="number">{{$t('gift_card.transaction_number')}}</th>
                <th class="amount">{{$t('loyalty_transactions.amount')}}</th>
                <th class="type">{{$t('loyalty_transactions.type')}}</th>
                <th class="date">{{$t('loyalty_transactions.date')}}</th>
                <th class="note">{{$t('note')}}</th>
                <th class="actions">{{$t('user_branch.actions')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(transaction, transactionIndex) of transactions" :key="'transaction' + transactionIndex">
                <td>#{{transaction.id}}</td>
                <td class="bold" :class="{add: transaction.money > 0, remove: transaction.money < 0}">{{transaction.money > 0 ? '+' : ''}}{{$tc(transaction.money)}}</td>
                <td>{{$t('gift_card.' + transaction.type)}}</td>
                <td>{{formatDateTime(transaction.date)}}</td>
                <td>
                  <template v-if="transaction.moreData && transaction.moreData.reason">{{transaction.moreData.reason}}</template>
                </td>
                <td>
                  <div class="actions">
                    <router-link target="_blank" :to="$router.buildContext('/sale', {id: transaction.sale})"
                                 class="btn btn-primary" v-if="transaction.sale">
                      {{$t('gift_card.view_sale')}}
                      <svgicon src="icon/link.svg" :height="12" :width="12"></svgicon>
                    </router-link>
                    <router-link target="_blank" :to="$router.buildContext('/customer', {id: transaction.createdBy})"
                                 class="btn btn-primary" v-if="transaction.createdBy && transaction.sale">
                      {{$t('gift_card.view_user')}}
                      <svgicon src="icon/link.svg" :height="12" :width="12"></svgicon>
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="empty" v-if="!transactions || transactions.length === 0">
          {{$t('gift_card.no_transaction')}}
        </div>
      </Panel>

      <div class="footer-actions">
        <button class="btn btn-secondary" @click="back">{{$t('back')}}</button>
      </div>

      <Modal name="adjustment-modal" :title="$t('gift_card.adjustment')"
             :actions="[{name: $t('back'), action: 'close'}, {name: $t('confirm'), action: sendAdjustment}]">
        <div class="group">
          <label>{{$t('loyalty_transactions.amount')}} ($)</label>
          <input type="number" step="0.01" v-model="adjustmentAmount" placeholder="0.00">
        </div>
        <div class="group">
          <label>{{$t('gift_card.reason')}}</label>
          <textarea v-model="adjustmentReason"></textarea>
        </div>
      </Modal>

    </template>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import InformationBlock from "@/components/tools/InformationBlock.vue";

export default {
  name: "giftCard",

  components: { InformationBlock },

  data() {
    return {
      giftCard: null,
      adjustmentAmount: null,
      adjustmentReason: "",
      transactions: []
    }
  },

  async mounted() {
    this.$store.state.pageTitleTranslationKey = "gift_card.single";
    this.$store.state.pageIcon = "icon/gift.svg";

    if (this.cardNumber) {
      showSpinner();
      await this.loadGiftCard();
      await this.loadTransactions();
      hideSpinner();
    }
  },

  computed: {
    cardNumber() {
      return this.$route.query.id
    },
    branchId() {
      return this.$route.query.branch;
    }
  },

  methods: {
    ...mapActions(["getGiftCard", "getGiftCardTransactions", "giftCardAdjustment"]),

    async loadGiftCard() {
      let response = await this.getGiftCard(this.cardNumber);
      if (response) {
        this.giftCard = response;
      }
    },

    adjustment() {
      this.$modal.show("adjustment-modal");
    },

    async sendAdjustment() {
      if (!this.adjustmentReason || !this.adjustmentAmount) {
        return;
      }
      showSpinner();
      let response = await this.giftCardAdjustment({cardId: this.giftCard.id, amount: this.adjustmentAmount, reason: this.adjustmentReason});
      hideSpinner();
      if (response) {
        this.loadGiftCard();
        this.loadTransactions();
        this.$modal.hide("adjustment-modal");
        toast(this.$t("success"), "success");
      } else {
        toast(this.$t("error.no_server"), "error");
      }
    },

    formatDateTime(date) {
      return window.formatDateTime("24", date);
    },

    async loadTransactions() {
      let response = await this.getGiftCardTransactions({branchId: this.branchId, number: this.cardNumber});
      if (response) {
        this.transactions = response;
      }
    },

    back() {
      this.$router.push(this.$router.buildContext("/gift-cards"));
    }
  }
}
</script>

<style lang="scss" scoped>
  #giftCard td .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0;

    .btn {
      display: flex;
      align-items: center;
      text-decoration: none;
      fill: white;
      font-size: 12px;
      padding: 6px 10px;
      white-space: nowrap;

      &:not(:last-child) {
        margin-right: 10px;
      }

      .svgicon {
        margin-left: 10px;
        margin-right: 0;
      }
    }
  }

  .empty {
    color: #888;
    font-style: italic;
  }

  .bold {
    font-weight: bold;
  }

  table {
    width: 100%;

    td, th {
      padding: 10px 10px;
      text-align: left;
    }
    th {
      color: #808080;
      font-weight: 400;
      font-size: 13px;
      vertical-align: bottom;

      &.number {
        width: 1px;
      }
      &.amount {
        width: 150px;
      }
      &.actions {
        width: 1px;
      }
    }
    td {
      font-size: 14px;

      &.add {
        color: #359933 !important;
      }
      &.remove {
        color: #cd3434 !important;
      }
    }
    tbody tr:nth-child(odd) {
      td {
        background-color: #f5f5f5;
        color: #1a1a1a;
      }
      td:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }
      td:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }
  }
</style>
